import moment from "moment";
import pdfMake from "../pdfmakeWrapper";
import { getSecureItem } from "../../../utilsFunctions";
import { ImageBaseUrl } from "../../../Environment";

// ── Helpers ───────────────────────────────────────────────────────────────────

const getBase64ImageFromUrl = (url) =>
  fetch(url, { mode: "cors" })
    .then((res) => {
      if (!res.ok) throw new Error("Network response was not ok");
      return res.blob();
    })
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        }),
    );

const getLabelByValue = (data, value) =>
  data?.find((item) => item.value === value)?.label || "-";

const formatAddress = (addr) =>
  [
    addr?.AddressStreet,
    addr?.AddressLandmark,
    addr?.AddressCity,
    addr?.AddressState,
    addr?.AddressPincode,
  ]
    .filter(Boolean)
    .join(", ");

const formatCurrency = (symbol, value) =>
  `${symbol} ${Number(value || 0).toLocaleString()}`;

// ── PDF column config ─────────────────────────────────────────────────────────

const COLUMN_WIDTHS = [25, 55, 90, 55, 70, 80, 70, 80, 70, 75, 75];
const TARGET_WIDTH = 720;

const scaledWidths = (() => {
  const total = COLUMN_WIDTHS.reduce((a, b) => a + b, 0);
  return COLUMN_WIDTHS.map((w) => Math.round((w / total) * TARGET_WIDTH));
})();

// ── Main export ───────────────────────────────────────────────────────────────

const PdfReportOutstandingDues = async ({
  sortedData,
  selectedCA,
  loanType,
  loanTypeData,
  loanTenure,
  companyData,
  cAgentData,
  boxData,
}) => {
  const currencySymbol = getSecureItem("country")?.symbol || "";

  const imageUrl =
    getSecureItem("loggedinUserCompany") === "1365"
      ? "https://i.imghippo.com/files/CCD2780qQ.jpeg"
      : `${ImageBaseUrl}${companyData?.profilePic}`;

  let imageBase64 = null;
  try {
    imageBase64 = await getBase64ImageFromUrl(imageUrl);
  } catch (err) {
    console.warn("Logo load failed, continuing without it:", err);
  }

  buildAndDownloadPdf(imageBase64);

  // ── Inner builder ──────────────────────────────────────────────────────────

  function buildAndDownloadPdf(logo) {
    // Header (company info)
    const headerSection = [
      {
        table: {
          widths: ["*"],
          body: [
            [
              {
                columns: [
                  logo
                    ? { image: logo, width: 60, height: 70 }
                    : {
                        text: "LOGO",
                        width: 60,
                        alignment: "center",
                        color: "#aaa",
                      },
                  {
                    width: "*",
                    alignment: "center",
                    stack: [
                      { text: companyData.companyName, style: "companyName" },
                      {
                        text: `Address: ${formatAddress(companyData.address)}`,
                        style: "infoText",
                      },
                      {
                        text: `GSTIN: ${companyData.GstIn || ""} | Website: ${companyData.companyWebsite || ""}`,
                        style: "infoText",
                      },
                      {
                        text: `Mobile: ${companyData.phone} | Email: ${companyData.companyMail}`,
                        style: "infoText",
                      },
                    ],
                  },
                ],
                columnGap: 10,
              },
            ],
          ],
        },
        layout: { hLineWidth: () => 1, vLineWidth: () => 1 },
        margin: [0, 0, 0, 10],
      },
    ];

    // Filter info
    const filterSection = [
      { text: "Outstanding Dues Report", style: "title" },
      {
        text: `Collection Agent: ${getLabelByValue(cAgentData, selectedCA) || "All"}`,
        style: "filter",
      },
      {
        text: `Loan Type: ${getLabelByValue(loanTypeData, loanType) || "All"}`,
        style: "filter",
      },
      {
        text: `Tenure: ${loanTenure || "All"}`,
        style: "filter",
        margin: [0, 0, 0, 20],
      },
    ].map((item) => ({ ...item, alignment: "center" }));

    // Summary cards row
    const summaryRow = {
      table: {
        widths: boxData.map(() => `${Math.floor(100 / boxData.length)}%`),
        body: [
          boxData.map((item, i) => ({
            stack: [
              { text: item.label, style: "fourBlockHeading" },
              {
                text:
                  i === 0
                    ? String(item.value)
                    : formatCurrency(currencySymbol, item.value),
                style: "fourBlockValue",
              },
            ],
            alignment: "center",
          })),
        ],
      },
      layout: { hLineWidth: () => 1, vLineWidth: () => 1 },
      margin: [0, 0, 0, 10],
    };

    // Data table
    const tableHeaders = [
      "Sr No",
      "Loan Id",
      "Customer Name\n(Customer Id)",
      "Area/Line",
      "Mobile Number",
      "Collection Agent",
      "Due Till Date",
      "Amount Paid Till Date",
      "Pending Due",
      "Last Payment Date",
      "Outstanding Balance",
    ];

    const tableBody = [
      tableHeaders.map((text) => ({
        text,
        style: "tableHeader",
        alignment: "center",
      })),
      ...sortedData.map((row, index) => {
        const name = `${row?.customer?.firstName || ""} ${row?.customer?.lastName || ""}`;
        const custId = row?.customer?.cust_id
          ? `(${row.customer.cust_id})`
          : "";

        return [
          index + 1,
          row.loanId,
          `${name}\n${custId}`,
          row?.customer?.areaLine || "-",
          row?.customer?.phone || "-",
          getLabelByValue(cAgentData, row?.collectorId),
          formatCurrency(currencySymbol, row?.totalDueTillDate),
          formatCurrency(currencySymbol, row?.totalCollectedTillDate),
          formatCurrency(currencySymbol, row?.pendingDue),
          row?.lastPaymentDate ? moment(row.lastPaymentDate).format("LL") : "-",
          formatCurrency(currencySymbol, row?.outstandingBalance),
        ];
      }),
    ];

    const docDefinition = {
      pageOrientation: "landscape",
      pageMargins: [10, 10, 10, 10],
      defaultStyle: { alignment: "center", fontSize: 9 },
      styles: {
        title: { fontSize: 16, bold: true, marginBottom: 10 },
        filter: { fontSize: 10, marginBottom: 2 },
        companyName: {
          fontSize: 14,
          bold: true,
          marginBottom: 4,
          marginTop: 5,
        },
        fourBlockHeading: { fontSize: 11, bold: true, margin: [0, 5, 0, 2] },
        fourBlockValue: { fontSize: 10, margin: [0, 2, 0, 5] },
        infoText: { fontSize: 9, lineHeight: 1.3 },
        tableHeader: { fontSize: 10, bold: true },
      },
      content: [
        ...headerSection,
        ...filterSection,
        summaryRow,
        {
          table: { headerRows: 1, widths: scaledWidths, body: tableBody },
          layout: {
            fillColor: (rowIndex) => (rowIndex === 0 ? "#f0f0f0" : null),
          },
        },
      ],
    };

    pdfMake.createPdf(docDefinition).download("OutstandingDuesReport.pdf");
  }
};

export default PdfReportOutstandingDues;
